import useLocalStorage from 'core/hooks/useLocalStorage'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import APIService from 'api/APIService'
import { JobStatus, OnboardingInvitePayload } from 'types/entity'
import Button from 'core/components/buttons/Button'
import useCurrentTeam from 'core/hooks/useCurrentTeam'
import { useSettings } from 'core/hooks/useSettings'
import useNotifications from 'core/hooks/useNotifications'
import SendInviteAction from 'pages/teams/members/onboarding-actions/SendInviteAction'
import { downloadFile } from 'utils/AppHelper'
import AnalyticsHelper from 'utils/AnalyticsHelper'
import CloudEvent from 'utils/CloudEvent'

interface BulkInviteProps {
  onSuccess?: VoidFunction
}

const useBatchInvite = ({ onSuccess }: BulkInviteProps = {}) => {
  const queryClient = useQueryClient()
  const { data: currentTeam } = useCurrentTeam()
  const { data: settings, set } = useSettings()
  const notification = useNotifications()

  const [localJobId, setJobId] = useLocalStorage(
    'member_batch_invite_job_id',
    ''
  )
  const jobID = settings.pollingJobs.memberBatchInviteJobId || localJobId

  useQuery(
    ['member-batch-invite-polling', jobID],
    () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    {
      enabled: !!jobID,
      refetchInterval: 2000,
      onSuccess: (response: JobStatus) => {
        if (response.status === 'finished') {
          notification.dismiss('memberBatchInviteJobId')
          setJobId('')
          set('pollingJobs', {
            ...settings.pollingJobs,
            memberBatchInviteJobId: ''
          })

          const memberTokenIds =
            (response.data?.member_token_ids as string[]) ?? []

          if (!memberTokenIds.length) {
            notification.success({
              title: 'Onboarding invite(s) sent',
              id: jobID
            })
          }

          const downloadButton = response.data?.file_url ? (
            <Button
              className="text-primary"
              variant="link"
              onClick={() => {
                downloadFile(response.data.file_url as string, 'errors_list')
              }}
            >
              Download list of errors
            </Button>
          ) : null

          const inviteButton = (
            <SendInviteAction
              selectedMembers={memberTokenIds}
              onlyMembers={false}
              forceInvite={false}
              className="text-primary"
              variant="link"
              buttonTitle="Re-send invites"
              onSuccess={() => {
                AnalyticsHelper.trackEvent(
                  CloudEvent.OnboardingTabBatchAction,
                  {
                    type: 're-send invites'
                  }
                )
              }}
            />
          )

          // Members failed and all of them failed
          if (memberTokenIds.length && !response?.data?.partial_success) {
            notification.error({
              title: `Onboarding invites failed to send`,
              subtitle: `Unable to send invites, download the list of errors to see what went wrong.`,
              id: jobID,
              footer: () => {
                return (
                  <div className="flex items-center gap-3">
                    {downloadButton}
                    {inviteButton}
                  </div>
                )
              }
            })
          }

          // Some success and some others failed
          if (memberTokenIds.length && response?.data?.partial_success) {
            notification.error({
              title: `${memberTokenIds.length} invites failed to send`,
              subtitle: `Some invites were sent successfully, but ${memberTokenIds.length} invites failed.  Download the list of errors to see what went wrong.`,
              id: jobID,
              footer: () => {
                return (
                  <div className="flex items-center gap-3">
                    {downloadButton}
                    {inviteButton}
                  </div>
                )
              }
            })
          }

          queryClient.invalidateQueries({ queryKey: ['members'] })
          queryClient.invalidateQueries({ queryKey: ['onboarding'] })
        }

        if (response.status === 'failed') {
          notification.dismiss('memberBatchInviteJobId')
          setJobId('')
          set('pollingJobs', {
            ...settings.pollingJobs,
            memberBatchInviteJobId: ''
          })
          notification.error({
            title: 'Could not invite members at this time',
            id: jobID
          })
        }
      },
      onError: () => {
        notification.dismiss('memberBatchInviteJobId')
        notification.error({
          title: 'Could not invite members at this time',
          id: jobID
        })
        setJobId('')
        set('pollingJobs', {
          ...settings.pollingJobs,
          memberBatchInviteJobId: ''
        })
      }
    }
  )

  const sendInviteQuery = useMutation(
    ['send', 'batch', 'invite', 'onboarding', currentTeam?.token_id],
    (payload: OnboardingInvitePayload) => {
      return APIService.members.batchInviteMembers({
        payload,
        teamId: currentTeam?.token_id ?? ''
      })
    },
    {
      onSuccess: (data) => {
        notification.loading({
          title: 'Sending invites...',
          subtitle:
            'This may take a few minutes. You can close this pop up and we’ll keep working in the background.',
          duration: Infinity,
          id: 'memberBatchInviteJobId'
        })
        setTimeout(() => {
          set('pollingJobs', {
            ...settings.pollingJobs,
            memberBatchInviteJobId: data.token_id
          })
        }, 0)
        setJobId(data.token_id)
        onSuccess?.()
      },
      onError: () => {
        notification.error('An error has occurred trying invite members')
      }
    }
  )

  return {
    sendInviteQuery
  }
}

export default useBatchInvite
